import React, { useEffect, useState, useRef } from 'react';
import { NavLink as RRNavLink, useLocation, useHistory } from 'react-router-dom';
import * as ROUTES from '../../constants/routes';
import SignOutButton from '../SignOut/SignOut';
import { AuthUserContext } from '../Session';
import logo from '../../assets/svg/logo.svg';
import {
  Navbar,
  NavItem,
  Nav,
  Container,
  NavbarToggler,
  Collapse,
  NavbarBrand,
} from 'reactstrap';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore } from '../../store/organization.store';
import OrganizationSwitcher from '../OrganizationSwitcher';
import NotificationBell from '../NotificationBell';

// Pages where the nav bar should not be shown (pre-auth and org selection pages)
const HIDE_NAV_ROUTES = [
  ROUTES.SELECT_ORGANIZATION,
  ROUTES.GET_STARTED,
  ROUTES.SIGN_IN,
  ROUTES.SIGN_UP,
  ROUTES.PASSWORD_FORGET,
];

const Navigation = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const location = useLocation();

  // Auto-close the mobile drawer on navigation so the destination page is visible.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Hide nav bar on pre-auth and org selection pages, but keep top spacing
  if (HIDE_NAV_ROUTES.includes(location.pathname)) {
    return <div style={{ paddingTop: '60px' }} />;
  }

  return (
  <Navbar expand="xl" className="pt-4">
    <Container fluid className="px-0">
      <NavbarBrand style={{ marginLeft: '0px', paddingLeft: '30px' }}>
        <img alt="Audio Militia" src={logo} />
        <strong className="d-block w-100 text-center font-weight-bold text-light">
          VAULT
        </strong>
      </NavbarBrand>
      <NavbarToggler onClick={toggle} />
      <Collapse isOpen={isOpen} navbar className="custom-navbar-collapse">
        <AuthUserContext.Consumer>
          {(authUser) => {
            return authUser ? (
              <NavigationAuth authUser={authUser} />
            ) : (
              <NavigationNonAuth />
            );
          }}
        </AuthUserContext.Consumer>
      </Collapse>
    </Container>
  </Navbar>
);
};

function NavigationAuth({ authUser }: any) {
  const [role, setAuth] = useAuthStore((state) => [
    state.role,
    state.setAuth,
  ]);

  // Get organization-specific role (overrides global role for multi-tenant)
  const currentOrgRole = useOrganizationStore(state => state.currentOrgRole);
  const hasOrganizations = useOrganizationStore(state => state.organizations.length > 0);
  const isSuperAdmin = useOrganizationStore(state => state.isSuperAdmin);

  useEffect(() => {
    if (authUser) {
      setAuth(authUser);
    }
  }, [authUser, setAuth]);

  // Use organization role if user has organizations, otherwise fall back to global role
  const effectiveRole = hasOrganizations ? currentOrgRole : role;
  // Super admins always have full access
  const canAddTracks = isSuperAdmin || ['ADMIN', 'OPERATOR'].includes(effectiveRole || '');

  return (
    <>
      {/* Identity cluster: user menu • org switcher • notification bell.
          First in source so the mobile drawer shows it at the top; on desktop
          it's pushed to the right via ml-auto. */}
      {(hasOrganizations || isSuperAdmin) && (
        <Nav navbar className="nav-identity">
          <NavItem className="nav-header-row">
            <UserMenu />
            <OrganizationSwitcher />
            <NotificationBell />
          </NavItem>
        </Nav>
      )}

      {/* Primary navigation — centered to the viewport on desktop, full-width list on mobile */}
      <Nav navbar className="nav-primary">
        <NavItem>
          <RRNavLink to={ROUTES.FIND_AUDIO} className="nav-link nav-link-with-icon">
            <i className="tim-icons icon-single-copy-04 nav-link-icon" />
            <span>Library</span>
          </RRNavLink>
        </NavItem>
        <NavItem>
          <RRNavLink to={ROUTES.PUBLIC_CATALOGUE} className="nav-link nav-link-with-icon">
            <i className="tim-icons icon-planet nav-link-icon" />
            <span>Public Tracks</span>
          </RRNavLink>
        </NavItem>
        {canAddTracks && (
          <NavItem>
            <RRNavLink to={ROUTES.ADD_AUDIO} className="nav-link nav-link-with-icon">
              <i className="tim-icons icon-cloud-upload-94 nav-link-icon" />
              <span>Add A Track</span>
            </RRNavLink>
          </NavItem>
        )}
      </Nav>
    </>
  );
}

function UserMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'rgba(255, 255, 255, 0.8)',
          padding: '8px 12px',
          fontSize: '16px',
        }}
      >
        <i className="tim-icons icon-single-02"></i>
      </button>
      {open && (
        <div className="org-switcher__menu" style={{
          position: 'absolute', top: '100%', right: 0,
          minWidth: '180px', marginTop: '8px',
          background: '#1e1e2f',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          zIndex: 1060,
          overflow: 'hidden',
        }}>
          <div style={{
            fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)',
            textTransform: 'uppercase', letterSpacing: '0.5px',
            padding: '8px 16px',
          }}>
            Account
          </div>
          <button
            onClick={() => { setOpen(false); history.push(ROUTES.ACCOUNT); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
              padding: '10px 16px', background: 'none', border: 'none',
              color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', cursor: 'pointer',
              textAlign: 'left', transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'; }}
          >
            <i className="tim-icons icon-settings-gear-63" style={{ fontSize: '14px' }}></i>
            Settings
          </button>
          <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', margin: '0' }} />
          <div style={{ padding: '6px 8px' }}>
            <SignOutButton />
          </div>
        </div>
      )}
    </div>
  );
}

function NavigationNonAuth() {
  return (
    <>
      {/* Navigation items for non-authenticated users if needed */}
    </>
  );
}

export default Navigation;
